import type { NoteSummary } from '@kanto/shared';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, errorText } from '../api/client';
import {
  useArchiveNote,
  useCreateFolder,
  useCreateNote,
  useDeleteFolder,
  useFolders,
  useNotes,
  useRestoreNote,
  useUpdateFolder,
  useUpdateNote,
} from '../api/hooks';
import { AppShell } from '../components/AppShell';
import { FolderTree } from '../components/FolderTree';
import { NoteRow } from '../components/NoteRow';
import { OfflineNotice } from '../components/OfflineNotice';
import { Panel } from '../components/Panel';
import { TagFacet } from '../components/TagFacet';
import { useT } from '../lib/i18n';

/**
 * The notes, the tree they are filed in, and the six states this screen can be
 * in — the same six as `/todos` and `/reminders`, in the same `Panel`, because
 * three lists that behave differently for no reason are three things to learn.
 *
 * **Everything is filtered here**, over the list that has already arrived: the
 * folder, the tag facet, and the counts beside each folder. One query brings the
 * summaries, so moving between folders costs nothing and cannot show one
 * folder's notes while another's are in flight. The bodies are not in that
 * query, which is what keeps it small enough for this to be true.
 *
 * The tree sits beside the list on a wide screen and above it on a phone. There
 * is no third breakpoint: at 375 px a column of folders over a list of notes is
 * legible, and a drawer for it would be a control to find before the notes.
 */

/** Uneven on purpose, as on the other two lists: bars of one length read as a table. */
const SKELETON_WIDTHS = ['68%', '45%', '77%', '52%'];

export default function NotesPage(): ReactElement {
  const t = useT();
  const navigate = useNavigate();
  const { data, error, isPending, fetchStatus, refetch } = useNotes();
  const { data: folderData } = useFolders();

  const create = useCreateNote();
  const update = useUpdateNote();
  const archive = useArchiveNote();
  const restore = useRestoreNote();
  const createFolder = useCreateFolder();
  const updateFolder = useUpdateFolder();
  const deleteFolder = useDeleteFolder();

  /** `undefined` is everything, `null` the inbox, a string one folder. */
  const [folder, setFolder] = useState<string | null | undefined>(undefined);
  const [tag, setTag] = useState<string | null>(null);
  const [archived, setArchived] = useState<string[]>([]);
  const [folderRefusal, setFolderRefusal] = useState<string | null>(null);

  const notes = data?.notes ?? [];
  const folders = folderData?.folders ?? [];

  const counts = new Map<string | null, number>();
  for (const note of notes) counts.set(note.folderId, (counts.get(note.folderId) ?? 0) + 1);

  const facetTags = [
    ...new Set([...notes.flatMap((note) => note.tags), ...(tag === null ? [] : [tag])]),
  ].sort();

  const shown = notes
    .filter((note) => folder === undefined || note.folderId === folder)
    .filter((note) => tag === null || note.tags.includes(tag));

  const onArchive = (id: string): void => {
    archive.mutate(id);
    // Remembered rather than read back from the list: the row is gone from it by
    // the time this runs.
    setArchived((ids) => [...ids, id]);
  };

  const onNewNote = (): void => {
    create.mutate(
      { folderId: folder ?? null },
      // Straight into the editor: a note created from this screen was created in
      // order to be written, and stopping at a blank row in a list is a step on
      // the way to the only thing anybody wanted.
      { onSuccess: (note) => void navigate(`/notes/${note.id}`) },
    );
  };

  const onDeleteFolder = (id: string): void => {
    setFolderRefusal(null);
    deleteFolder.mutate(id, {
      onError: (failure) =>
        setFolderRefusal(
          failure instanceof ApiError && failure.code === 'folder_not_empty'
            ? failure.message
            : errorText(failure, t('notes.folderFailed')),
        ),
      onSuccess: () => {
        if (folder === id) setFolder(undefined);
      },
    });
  };

  /**
   * Offline, rather than broken. The same two signals the other two lists read,
   * and for the same reason — see `TodosPage` for the full reasoning; repeating
   * it here would be a third copy to keep in step.
   */
  if ((fetchStatus === 'paused' || !navigator.onLine) && !data) {
    return (
      <Screen>
        <OfflineNotice />
      </Screen>
    );
  }

  if (isPending) {
    return (
      <Screen>
        <ul
          className="animate-pulse divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface"
          role="status"
          aria-label={t('common.loadingLabel')}
        >
          {SKELETON_WIDTHS.map((width, row) => (
            <li key={row} className="px-4 py-3">
              <span className="block h-4 rounded bg-line" style={{ width }} />
              <span className="mt-2 block h-3 w-1/3 rounded bg-line" />
            </li>
          ))}
        </ul>
      </Screen>
    );
  }

  if (error) {
    return (
      <Screen>
        <Panel
          tone="alert"
          title={t('notes.errorTitle')}
          body={errorText(error, t('notes.errorFallback'))}
          icon={
            <path d="M12 8v5M12 16h.01M10.3 3.9 2.4 17a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          }
          action={
            <button
              type="button"
              onClick={() => void refetch()}
              className="min-h-11 rounded-lg bg-accent px-4 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-dim"
            >
              {t('notes.retry')}
            </button>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen onNewNote={onNewNote} creating={create.isPending}>
      <div className="lg:flex lg:items-start lg:gap-8">
        <div className="lg:w-56 lg:shrink-0">
          <FolderTree
            folders={folders}
            counts={counts}
            selected={folder}
            onSelect={setFolder}
            onCreate={(name, parentId) => {
              setFolderRefusal(null);
              createFolder.mutate(
                { name, parentId },
                {
                  onError: (failure) =>
                    setFolderRefusal(errorText(failure, t('notes.folderFailed'))),
                },
              );
            }}
            onRename={(id, name) => {
              setFolderRefusal(null);
              updateFolder.mutate(
                { id, patch: { name } },
                {
                  onError: (failure) =>
                    setFolderRefusal(errorText(failure, t('notes.folderFailed'))),
                },
              );
            }}
            onDelete={onDeleteFolder}
            busy={createFolder.isPending || updateFolder.isPending || deleteFolder.isPending}
          />
          {folderRefusal !== null && (
            <p role="alert" className="mt-2 px-2 text-xs text-alert">
              {folderRefusal}
            </p>
          )}
        </div>

        <div className="mt-6 min-w-0 flex-1 lg:mt-0">
          {archived.length > 0 && (
            <div className="mb-3 flex items-center gap-3 rounded-lg border border-line bg-accent-soft px-4 py-2.5 text-sm">
              <span className="flex-1 text-ink-700">{t('notes.archived', archived.length)}</span>
              <button
                type="button"
                onClick={() => {
                  for (const id of archived) restore.mutate(id);
                  setArchived([]);
                }}
                className="min-h-9 rounded-lg px-2 font-semibold text-accent hover:underline"
              >
                {t('notes.undo')}
              </button>
              <button
                type="button"
                onClick={() => setArchived([])}
                aria-label={t('notes.dismiss')}
                className="flex min-h-9 min-w-9 items-center justify-center rounded-lg text-ink-400 hover:text-ink-900"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="size-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </div>
          )}

          <TagFacet tags={facetTags} selected={tag} onSelect={setTag} />

          {notes.length === 0 && (
            <Panel
              tone="quiet"
              dashed
              title={t('notes.emptyTitle')}
              body={t('notes.emptyBody')}
              icon={<NoteIcon />}
            />
          )}

          {/* Notes exist and none of them is here. Not the empty state: an area
              left blank reads as one that failed to draw. */}
          {notes.length > 0 && shown.length === 0 && (
            <Panel
              tone="quiet"
              dashed
              title={t('notes.nothingHere')}
              body={t('notes.nothingHereBody')}
              icon={<NoteIcon />}
            />
          )}

          {shown.length > 0 && (
            <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface">
              {shown.map((note: NoteSummary) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  folders={folders}
                  onMove={(folderId) => update.mutate({ id: note.id, patch: { folderId } })}
                  onArchive={() => onArchive(note.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </Screen>
  );
}

/** The same mark the rail carries for this module, so the two read as one thing. */
function NoteIcon(): ReactElement {
  return (
    <>
      <path d="M5 4h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
      <path d="M14 4v5h5" />
    </>
  );
}

function Screen({
  children,
  onNewNote,
  creating,
}: {
  children: ReactNode;
  onNewNote?: () => void;
  creating?: boolean;
}): ReactElement {
  const t = useT();
  return (
    <AppShell>
      <div className="mb-4 flex items-center gap-3">
        <h1 className="flex-1 text-2xl font-semibold tracking-tight text-ink-900">
          {t('module.notes')}
        </h1>
        {onNewNote && (
          <button
            type="button"
            onClick={onNewNote}
            disabled={creating}
            className="flex min-h-9 items-center gap-1.5 rounded-lg bg-accent px-3 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-dim disabled:opacity-60"
          >
            <svg
              viewBox="0 0 24 24"
              className="size-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            {t('notes.new')}
          </button>
        )}
      </div>
      {children}
    </AppShell>
  );
}
